/**
 * AddRoadmapForm
 *
 * Body of the Add-idea page (`AddRoadmapPage`): the intro disclaimer, the
 * title field (max 100 chars), the description field (max 300 chars), and the
 * "Done" submit button. Ports the `contentWidget` of Flutter's
 * `AddRoadmapItemScreen` (`add_roadmap_item_screen.dart:173-322`):
 *
 *   - intro paragraph  → `add_roadmap_item_screen.dart:198-207`
 *   - title field      → `add_roadmap_item_screen.dart:209-247` (height 100,
 *                        max 100, hint "Idea title")
 *   - description field→ `add_roadmap_item_screen.dart:249-284` (height 100,
 *                        max 300, hint "Idea description")
 *   - submit button    → `add_roadmap_item_screen.dart:286-322` (enabled color
 *                        `exclusive`, disabled `backgroundColorLight`)
 *
 * Co-located with `AddRoadmapPage` to keep the page under the ~200-line
 * Should-Fix threshold from `presentation.md`.
 *
 * UI conventions deviate from Flutter per `presentation.md`: the field
 * background uses `theme.secondary` (the established web text-field surface,
 * matching `ContactRequestForm`) in place of Flutter's translucent
 * `backgroundColorLight`, and the enabled/disabled submit intent is carried
 * via the `theme.exclusive` / `theme.disabled` tokens.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InputText } from '@/presentation/components/common/InputText';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';

export interface AddRoadmapFormProps {
    title: string;
    description: string;
    canSubmit: boolean;
    setTitle: (value: string) => void;
    setDescription: (value: string) => void;
    onSubmit: () => void;
}

export const AddRoadmapForm = ({
    title,
    description,
    canSubmit,
    setTitle,
    setDescription,
    onSubmit,
}: AddRoadmapFormProps) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();

    const fieldWrapperStyle: React.CSSProperties = {
        backgroundColor: theme.secondary,
        borderRadius: responsive.borderRadius.base,
        overflow: 'hidden',
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: responsive.spacing['2xl'],
            }}
        >
            {/* Intro disclaimer (add_roadmap_item_screen.dart:198-207). */}
            <p
                style={{
                    margin: 0,
                    color: theme.text.secondary,
                    fontSize: responsive.font.base,
                    textAlign: 'center',
                }}
            >
                {l10n.roadmap.addIntro}
            </p>

            {/* Title field — max 100 chars (add_roadmap_item_screen.dart:209-247). */}
            <div style={fieldWrapperStyle}>
                <InputText
                    qa={{ id: 'add-roadmap-title' }}
                    value={title}
                    onChange={setTitle}
                    maxLength={InputLengthFormatters.roadmapTitleMaxLength}
                    placeholder={l10n.roadmap.ideaTitleHint}
                    multiline
                    backgroundColor="transparent"
                    height={responsive.form.textareaHeight}
                    padding={responsive.spacing.lg}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                />
            </div>

            {/* Description field — max 300 chars (add_roadmap_item_screen.dart:249-284). */}
            <div style={fieldWrapperStyle}>
                <InputText
                    qa={{ id: 'add-roadmap-description' }}
                    value={description}
                    onChange={setDescription}
                    maxLength={InputLengthFormatters.roadmapDescriptionMaxLength}
                    placeholder={l10n.roadmap.ideaDescriptionHint}
                    multiline
                    backgroundColor="transparent"
                    height={responsive.form.textareaHeight}
                    padding={responsive.spacing.lg}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                />
            </div>

            {/* "Done" submit — enabled `exclusive`, disabled `disabled`
                (add_roadmap_item_screen.dart:286-322). */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: responsive.spacing.lg,
                }}
            >
                <ExpauseButton
                    {...qaAttr({ id: 'add-roadmap-submit' })}
                    variant={ButtonVariant.primary}
                    backgroundColor={canSubmit ? theme.exclusive : theme.disabled}
                    disabled={!canSubmit}
                    onClick={onSubmit}
                >
                    {l10n.roadmap.submitDoneLabel}
                </ExpauseButton>
            </div>
        </div>
    );
};
